import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlayControllerVoiceV1, usePlayControllerMarkVoiceSeenV1 } from '@kidshome/api-contract';
import { useFeature } from '@/entities/session';
import type { EquippedLook } from '@/entities/cosmetics';
import { Feature } from '@/shared/lib/features';
import { HeroAvatar } from '@/entities/hero';
import { coinGlyphText } from '@/shared/ui';
import { renderVoiceLine, idleGreeting } from '@/widgets/narrator';

/**
 * HeroPresenceVoice (ADR-0095 §4) — the Home presence strip's "<hero> says" bubble (the mockup's
 * `.toast`). Unlike the bare {@link NarratorBubble}, it shows the chosen hero's FACE alongside the line
 * and a "<name> says" attribution caption, restoring the warmth/identity the flat box dropped. It owns
 * the same live feed (`GET /v1/play/voice`) + idempotent seen-marking (`POST /v1/play/voice/seen`) as the
 * shared bubble, so it is the single voice surface on Home (the seen cursor advances exactly once). When
 * nothing is fresh it shows the calm idle greeting. Reduced-motion-safe via the global `.toast` css.
 */
export function HeroPresenceVoice({
  archetypeId,
  stageIndex,
  look,
  name,
  embedded = false,
}: {
  archetypeId: string | null;
  stageIndex: number;
  /** The hero's equipped cosmetics, so the bubble face matches the Home strip + wardrobe. */
  look?: EquippedLook;
  /** The hero's display name, for the "<name> says" attribution caption. */
  name: string;
  /**
   * When embedded inside the Home hero card, render a bare speech bubble (no standalone `.toast` chrome,
   * no avatar — the card header right above already shows the hero face). Standalone surfaces (the learn
   * session) keep the full toast with the hero's face.
   */
  embedded?: boolean;
}): React.ReactElement {
  const { t } = useTranslation('play');
  const hasFeature = useFeature();
  const playEnabled = hasFeature(Feature.Play);

  const voice = usePlayControllerVoiceV1(undefined, {
    query: { enabled: playEnabled },
  });
  // Destructure the STABLE `mutate` (the mutation object is a fresh reference each render) so the
  // mark-seen effect runs only when the reaction changes, not on every render.
  const { mutate: markVoiceSeen } = usePlayControllerMarkVoiceSeenV1();

  const latest = voice.data?.latest ?? null;
  const liveLine = latest ? renderVoiceLine(t, latest) : null;
  // When nothing fresh, fall back to the authored idle greeting (matches the mockup + narrator system),
  // not the generic `hero.narrator.greeting`.
  const text = liveLine ?? idleGreeting(t, archetypeId);

  // When a fresh reaction is shown, mark the feed seen up to it (idempotent; offline replays re-send
  // safely). Guarded so we only fire once per reaction id.
  const seenRef = useRef<string | null>(null);
  useEffect(() => {
    if (latest && !latest.seen && seenRef.current !== latest.reactionId) {
      seenRef.current = latest.reactionId;
      markVoiceSeen({ data: { upToReactionId: latest.reactionId } });
    }
  }, [latest, markVoiceSeen]);

  // Embedded in the merged Home hero card: a bare tinted speech bubble, no second avatar.
  if (embedded) {
    return (
      <div className="hero-says" role="status">
        <p>{coinGlyphText(text)}</p>
        <small>{t('hero.narrator.says', { name })}</small>
      </div>
    );
  }

  return (
    <div className="toast" role="status">
      <HeroAvatar archetypeId={archetypeId} stageIndex={stageIndex} look={look} size={38} />
      <div>
        <p>{coinGlyphText(text)}</p>
        <small>{t('hero.narrator.says', { name })}</small>
      </div>
    </div>
  );
}
